import { AppError } from "../errors/index.js";
/**
 * ترتيب العناصر — **مفتاحٌ بين جارَين، لا عدّاد يُزاح**.
 *
 * هذا الملف يُلغي صنفاً كاملاً من عطب v2، لا يعالج حالةً منه:
 *
 * - كان السحب لأسفل داخل نفس العمود **يُنزل الكارت في الخانة الخطأ مراراً**،
 *   لأن الواجهة تحسب الهدف بـ«عدد الكروت فوق نقطة الإفلات» والخلفية تُزيح
 *   بـ«قيمة الموضع المخزَّنة». والدرس المسجَّل هناك حرفياً: **«عدد ما فوق» و
 *   «قيمة الموضع» نظامان إحداثيّان مختلفان.**
 * - وكان خروج الكارت من عموده **لا يُرَصّ بعده**، فتبقى فراغات؛ وحذفُ عمودٍ
 *   ينقل كروته **بمواضعها القديمة فتتصادم**؛ فاضطُرّ كل استعلام ترتيبٍ في
 *   المشروع أن يفصل التعادل بالمعرّف — علاجٌ في مكان القراءة لعطبٍ في الكتابة.
 * - وكانت الإزاحةُ **معفاةً من حرس النسخة بقصدٍ موثَّق**، فمحرّكان متزامنان
 *   يتشابكان وينتجان مواضع متكرّرة.
 *
 * والمفتاح الكسري يجعل الثلاثة بلا موضوع: **الإدراج يكتب صفّاً واحداً**، ولا
 * يمسّ جاراً، فلا إزاحة ولا تعادل ولا تشابك. والحركة تُوصَف بجارَيها لا برقمها،
 * فلا نظامَ إحداثيات ثانياً يُترجَم إليه.
 */
/**
 * أبجدية المفتاح — أرقامٌ ثم حروفٌ صغيرة، بترتيبٍ نصّي هو نفسه ترتيبُها هنا.
 *
 * والترتيب في قاعدة البيانات يكون **بالمقارنة النصّية المجرّدة** (`C` collation)
 * لا بترتيب لغةٍ بعينها — فترتيب العربية أو التركية لا يغيّر مواضع الكروت.
 */
const ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyz";
const FIRST = ALPHABET[0];
const LAST = ALPHABET[ALPHABET.length - 1];
const BASE = ALPHABET.length;
/** أقصى طول يُسمح به — يحمي من نموٍّ غير محدود بسحبٍ متكرّر في نفس الشقّ */
export const MAX_ORDER_KEY_LENGTH = 64;
/** أوّل مفتاح في قائمةٍ فارغة — وسطُ المدى، فيبقى ما قبله وما بعده مفتوحاً */
export const FIRST_ORDER_KEY = "n";
function indexOf(char) {
    const index = ALPHABET.indexOf(char);
    if (index < 0) {
        throw new AppError("INTERNAL", {
            context: { reason: "order-key-invalid-character", char },
        });
    }
    return index;
}
/** هل هذا نصٌّ صالحٌ كمفتاح ترتيب؟ — يُستعمل في التحقّق من مدخلٍ خارجي */
export function isOrderKey(value) {
    if (value.length === 0 || value.length > MAX_ORDER_KEY_LENGTH)
        return false;
    if (!/^[0-9a-z]+$/.test(value))
        return false;
    // مفتاحٌ ينتهي بأصغر رمز لا يترك شقّاً قبله، فلا يُسمح به
    return !value.endsWith(FIRST);
}
/**
 * مفتاحٌ يقع **بين** جارَين — وأحدهما أو كلاهما قد يغيب عند الطرفين.
 *
 * | before | after | المعنى                  |
 * | ------ | ----- | ----------------------- |
 * | `null` | `null`| قائمة فارغة             |
 * | `null` | مفتاح | إدراجٌ في الأوّل         |
 * | مفتاح  | `null`| إدراجٌ في الآخر          |
 * | مفتاح  | مفتاح | إدراجٌ بينهما            |
 *
 * **ولا يمسّ هذا شيئاً غير الصفّ المُدرَج.** هذا هو الفرق كلّه.
 */
export function orderKeyBetween(before, after) {
    if (before !== null && after !== null && before >= after) {
        throw new AppError("INTERNAL", {
            context: { reason: "order-key-neighbours-out-of-order", before, after },
        });
    }
    if (before === null && after === null)
        return FIRST_ORDER_KEY;
    if (before === null)
        return keyBefore(after);
    if (after === null)
        return keyAfter(before);
    return keyBetween(before, after);
}
/** أصغر من `after` — بإنقاص أوّل رمزٍ يمكن إنقاصه، وإلّا بالتفريع تحته */
function keyBefore(after) {
    const head = indexOf(after[0]);
    if (head > 1) {
        // يبقى شقٌّ فوق أصغر رمز، فنأخذ نصف المسافة إلى بدايته
        return ALPHABET[Math.floor(head / 2)];
    }
    // `after` عند أوّل المدى: نمدّ بمفتاحٍ أطول يقع قبله ولا يخرج من المدى
    return keyBetween(FIRST, after);
}
/** أكبر من `before` — بزيادة أوّل رمزٍ يمكن زيادته، وإلّا بالإلحاق */
function keyAfter(before) {
    const head = indexOf(before[0]);
    if (before.length === 1 && head < BASE - 1) {
        return ALPHABET[head + Math.max(1, Math.floor((BASE - head) / 2))];
    }
    if (before[0] === LAST) {
        // عند آخر المدى: نمدّ الطول، فيبقى `before` أصغر نصّياً لأنه بادئةٌ له
        return before + FIRST_ORDER_KEY;
    }
    return keyBetween(before, ALPHABET[head + 1]);
}
/**
 * مفتاحٌ بين نصَّين — يمشي على الرموز المشتركة ثم يشقّ ما بينهما.
 *
 * وحين لا يبقى شقّ (جارَان متلاصقان كـ`ab` و`ac`)، **يمدّ الطول** بدل أن يُزيح
 * أحدهما — وهذه هي النقطة: الإدراج لا يكتب في صفٍّ آخر أبداً.
 */
function keyBetween(before, after) {
    let prefix = "";
    let i = 0;
    for (;;) {
        const b = i < before.length ? indexOf(before[i]) : -1;
        const a = i < after.length ? indexOf(after[i]) : BASE;
        if (b === a) {
            prefix += ALPHABET[b];
            i += 1;
            continue;
        }
        if (a - b > 1) {
            const middle = b + Math.floor((a - b) / 2);
            return guardLength(prefix + ALPHABET[middle]);
        }
        // متلاصقان: نثبّت رمز `before` ونكمل البحث في بقيّته مقابل آخر المدى
        prefix += ALPHABET[Math.max(b, 0)];
        const rest = before.slice(i + 1);
        return guardLength(prefix + (rest === "" ? FIRST_ORDER_KEY : keyAfter(rest)));
    }
}
/**
 * يرفض المفتاح الطويل **بكودٍ يُقرأ**، لا بصمتٍ ولا بخطأٍ عامّ.
 *
 * وهو الحدّ الوحيد للمفتاح الكسري، ويُبلَغ بسحبٍ متكرّرٍ في نفس الشقّ آلافَ
 * المرات. وعلاجه إعادةُ توزيعٍ للعمود — عمليةٌ معلَنة، لا إزاحةٌ صامتة.
 */
function guardLength(key) {
    if (key.length > MAX_ORDER_KEY_LENGTH) {
        throw new AppError("ORDER_KEY_EXHAUSTED", {
            context: { maxLength: String(MAX_ORDER_KEY_LENGTH) },
        });
    }
    return key;
}
/**
 * مفاتيح متتالية لعناصرٍ تُنشأ معاً — أعمدةُ قالبٍ مثلاً.
 *
 * وتُوزَّع على المدى بتباعدٍ متساوٍ، فيبقى بين كل جارَين شقٌّ واسعٌ للإدراج.
 */
export function spreadOrderKeys(count) {
    if (count <= 0)
        return [];
    const keys = [];
    const step = Math.max(1, Math.floor(BASE / (count + 1)));
    for (let i = 1; i <= count; i += 1) {
        const at = Math.min(BASE - 1, i * step);
        keys.push(ALPHABET[at]);
    }
    // تباعدٌ متساوٍ قد يتكرّر عند أعدادٍ كبيرة — فنملأ ما بين المتكرّرين
    for (let i = 1; i < keys.length; i += 1) {
        if (keys[i] <= keys[i - 1]) {
            keys[i] = orderKeyBetween(keys[i - 1], keys[i + 1] ?? null);
        }
    }
    return keys;
}
//# sourceMappingURL=index.js.map